import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

export interface VerifyMfaLoginData {
  email: string;
  code: string;
}

export interface VerifyMfaLoginResult<T = unknown> {
  accessToken: string;
  refreshToken: string;
  user: T | null | undefined;
}

export interface MfaSetupResult {
  qrCodeBase64: string;
  manualEntryKey: string;
  message: string;
}

export interface VerifyMfaSetupResult {
  message: string;
  backupCodes: string[];
}

export interface DisableMfaResult {
  message: string;
}

/**
 * Verifies MFA during login and returns normalized auth data.
 */
export function useVerifyMfaMutation<T = unknown>(
  verifyMfaFn: (data: VerifyMfaLoginData) => Promise<VerifyMfaLoginResult<T>>,
  options?: Omit<
    UseMutationOptions<VerifyMfaLoginResult<T>, Error, VerifyMfaLoginData>,
    "mutationFn"
  >,
) {
  return useMutation({
    mutationFn: verifyMfaFn,
    ...options,
  });
}

/**
 * Starts MFA enrollment for the authenticated user.
 */
export function useSetupMfaMutation(
  setupMfaFn: () => Promise<MfaSetupResult>,
  options?: Omit<UseMutationOptions<MfaSetupResult, Error, void>, "mutationFn">,
) {
  return useMutation({
    mutationFn: setupMfaFn,
    ...options,
  });
}

/**
 * Confirms MFA enrollment with a verification code.
 */
export function useVerifyMfaSetupMutation(
  verifyMfaSetupFn: (code: string) => Promise<VerifyMfaSetupResult>,
  options?: Omit<
    UseMutationOptions<VerifyMfaSetupResult, Error, string>,
    "mutationFn"
  >,
) {
  return useMutation({
    mutationFn: verifyMfaSetupFn,
    ...options,
  });
}

/**
 * Disables MFA for the authenticated user.
 */
export function useDisableMfaMutation(
  disableMfaFn: (code: string) => Promise<DisableMfaResult>,
  options?: Omit<UseMutationOptions<DisableMfaResult, Error, string>, "mutationFn">,
) {
  return useMutation({
    mutationFn: disableMfaFn,
    ...options,
  });
}
